import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Chip
} from '@mui/material';
import { 
  CheckCircle as CheckIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../utils/LanguageContext';
import Layout from '../components/layout/Layout';
import contactInfo from '../config/contact';
import QuickOrderSection from '../components/home/QuickOrderSection';

const ServicesPage: React.FC = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const whatsappUrl = `https://wa.me/${contactInfo.whatsapp.number}?text=${encodeURIComponent(contactInfo.whatsapp.message[language as keyof typeof contactInfo.whatsapp.message])}`;

  const scrollToQuickOrder = () => {
    const element = document.getElementById('quick-order-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const servicesData = [
    {
      id: 'appDevelopment',
      icon: '📱',
      color: '#4A90E2',
      features: [
        'nativeDevelopment',
        'crossPlatform',
        'storeDeployment',
        'backendIntegration',
        'pushNotifications'
      ],
      technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Firebase', 'AWS'],
      deliveryTime: '2-8 weeks',
      startingPrice: 'HK$15,000'
    },
    {
      id: 'webDevelopment',
      icon: '🌐',
      color: '#5C6BC0',
      features: [
        'responsiveDesign',
        'modernFrameworks',
        'ecommerce',
        'cms',
        'seo'
      ],
      technologies: ['React', 'Vue.js', 'Next.js', 'WordPress', 'Shopify', 'Node.js'],
      deliveryTime: '1-6 weeks',
      startingPrice: 'HK$8,000'
    },
    {
      id: 'mobileDevelopment',
      icon: '📲',
      color: '#66BB6A',
      features: [
        'customSolutions',
        'pwa',
        'mobileFirst',
        'touchOptimized',
        'deviceFeatures'
      ],
      technologies: ['React Native', 'Flutter', 'Ionic', 'Xamarin', 'PWA'],
      deliveryTime: '2-10 weeks',
      startingPrice: 'HK$12,000'
    },
    {
      id: 'cloudServices',
      icon: '☁️',
      color: '#FF7043',
      features: [
        'infrastructure',
        'database',
        'apiDevelopment',
        'scalableArch',
        'devops'
      ],
      technologies: ['AWS', 'Google Cloud', 'Azure', 'Docker', 'Kubernetes', 'MongoDB'],
      deliveryTime: '1-4 weeks',
      startingPrice: 'HK$10,000'
    },
    {
      id: 'technicalSupport',
      icon: '🛠️',
      color: '#AB47BC',
      features: [
        'support247',
        'maintenance',
        'monitoring',
        'security',
        'enhancement'
      ],
      technologies: ['All platforms', 'Monitoring tools', 'Support systems'],
      deliveryTime: 'Ongoing',
      startingPrice: 'HK$3,000/month'
    },
    {
      id: 'uiUxDesign',
      icon: '🎨',
      color: '#EC407A',
      features: [
        'interfaceDesign',
        'uxResearch',
        'wireframing',
        'designSystem',
        'usability'
      ],
      technologies: ['Figma', 'Adobe XD', 'Sketch', 'Principle', 'InVision'],
      deliveryTime: '1-4 weeks',
      startingPrice: 'HK$5,000'
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          background: 'linear-gradient(135deg, rgba(18,18,18,0.9) 0%, rgba(26,32,48,1) 100%)',
          borderRadius: 0,
          mb: 6,
          position: 'relative',
          overflow: 'hidden',
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)',
          marginRight: 'calc(-50vw + 50%)'
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box textAlign="center">
            <Typography
              variant="h1"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                background: 'linear-gradient(45deg, #4A90E2 30%, #5C6BC0 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              {t('services.title')}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{
                maxWidth: 600,
                mx: 'auto',
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              {t('services.description')}
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={scrollToQuickOrder}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                background: 'linear-gradient(45deg, #4A90E2 30%, #5C6BC0 90%)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(74, 144, 226, 0.3)',
                }
              }}
            >
              {t('home.hero.cta')}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Services Grid - 3x2 Layout */}
      <Container maxWidth="lg">
        <Box 
          sx={{ 
            mb: 8,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)'
            },
            gridTemplateRows: {
              md: 'repeat(2, 1fr)'
            },
            gap: 4
          }}
        >
          {servicesData.map((service) => (
            <Box 
              key={service.id} 
              sx={{ 
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Card
                sx={{
                  width: '100%',
                  maxWidth: { xs: 380, md: '100%' },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 20px 40px rgba(${parseInt(service.color.slice(1, 3), 16)}, ${parseInt(service.color.slice(3, 5), 16)}, ${parseInt(service.color.slice(5, 7), 16)}, 0.3)`,
                    border: `1px solid ${service.color}50`,
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  {/* Service Header */}
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        fontSize: '3rem',
                        mb: 2,
                        p: 2,
                        borderRadius: 3,
                        background: `linear-gradient(45deg, ${service.color}20, ${service.color}40)`,
                        display: 'inline-block',
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                      {t(`services.${service.id}.title`)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t(`services.${service.id}.shortDescription`)}
                    </Typography>
                  </Box>

                  {/* Pricing & Timeline */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                    <Chip
                      label={`From ${service.startingPrice}`}
                      size="small"
                      sx={{
                        bgcolor: `${service.color}20`,
                        color: service.color,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                    <Chip
                      label={service.deliveryTime}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: service.color,
                        color: service.color,
                        fontSize: '0.75rem',
                      }}
                    />
                  </Box>

                  {/* Features */}
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                    {t('services.whatsIncluded')}
                  </Typography>
                  <List dense sx={{ mb: 2 }}>
                    {service.features.map((feature, index) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <CheckIcon sx={{ color: service.color, fontSize: 16 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={t(`services.features.${service.id}.${feature}`)}
                          sx={{ '& .MuiListItemText-primary': { fontSize: '0.85rem' } }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  {/* Technologies */}
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                    Technologies:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                    {service.technologies.slice(0, 4).map((tech, index) => (
                      <Chip
                        key={index}
                        label={tech}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                          height: 24,
                        }}
                      />
                    ))}
                    {service.technologies.length > 4 && (
                      <Chip
                        label={`+${service.technologies.length - 4}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                          height: 24,
                        }}
                      />
                    )}
                  </Box>

                  {/* CTA Button */}
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={scrollToQuickOrder}
                    sx={{
                      py: 1.2,
                      background: `linear-gradient(45deg, ${service.color}, ${service.color}DD)`,
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      '&:hover': {
                        background: `linear-gradient(45deg, ${service.color}DD, ${service.color})`,
                        transform: 'translateY(-2px)',
                      }
                    }}
                  >
                    {t('common.getQuote')}
                  </Button>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      </Container>

      <QuickOrderSection />
    </Layout>
  );
};

export default ServicesPage; 